import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadSimple, FileCsv } from "@phosphor-icons/react";

export default function EarningsDebug() {
  const [run, setRun] = useState(null);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const load = () => api.get("/admin/sync/earnings/latest").then(({ data }) => { setRun(data.run); setCount(data.toolost_report_count); });
  useEffect(() => { load(); }, []);

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/reports/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.format === "toolost_sales") toast.success(`Too Lost report: ${data.releases_matched} release(s) · $${data.total_royalty_usd.toFixed(2)} mapped`);
      else toast.success(`Generic CSV: ${data.inserted} inserted · ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="border border-zinc-800 mb-8" data-testid="earnings-import">
      <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-950 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
        Too Lost Earnings · {count} report row{count === 1 ? "" : "s"} in artist dashboards{run?.at && ` · last import ${new Date(run.at).toLocaleDateString()}`}
      </div>
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-5 p-6 border-r border-zinc-800 text-xs text-zinc-500 leading-relaxed space-y-3">
          <p>Too Lost's API doesn't expose earnings — royalties arrive as CSV <span className="text-white">Sales Reports</span>. Import them here and every artist's balance, splits and analytics update automatically. Re-importing the same months never double-counts.</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Log in at <span className="text-white">toolost.com</span> → <span className="text-white">Accounting</span></li>
            <li>Download the <span className="text-white">(Sales) Monthly Report</span> or <span className="text-white">Six Month Report</span> CSV</li>
            <li>Drop the file here</li>
          </ol>
        </div>
        <div className="lg:col-span-7 p-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); importFile(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()} data-testid="toolost-report-dropzone"
            className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-red-600 bg-red-600/5" : "border-zinc-700 hover:border-zinc-500"}`}>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="toolost-report-input" onChange={(e) => importFile(e.target.files?.[0])} />
            {busy ? <div className="text-xs tracking-widest uppercase text-zinc-400">Importing…</div> : (<>
              <UploadSimple size={28} className="mx-auto text-red-500 mb-3" />
              <div className="font-display text-xl uppercase tracking-tight">Import Too Lost Sales Report</div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-1">Drop CSV or click to browse</div>
            </>)}
          </div>
          {result && (
            <div className="mt-4 border border-zinc-800 p-4 text-xs space-y-2" data-testid="earnings-import-result">
              <div className="flex items-center gap-2 text-zinc-400"><FileCsv size={14} /> {result.format === "toolost_sales" ? "Too Lost Sales Report detected" : "Generic report CSV"} · {result.rows ?? result.inserted + result.skipped} rows</div>
              <div className="flex flex-wrap gap-4 font-mono">
                {result.releases_matched !== undefined && <span className="text-green-500">{result.releases_matched} releases matched</span>}
                <span className="text-green-500">{result.inserted} new</span>
                {result.updated !== undefined && <span className="text-blue-400">{result.updated} updated</span>}
                <span className={result.skipped ? "text-yellow-500" : "text-zinc-500"}>{result.skipped} skipped</span>
                {result.total_royalty_usd !== undefined && <span className="text-white">${result.total_royalty_usd.toFixed(2)} total</span>}
              </div>
              {result.errors?.length > 0 && <div className="border-t border-zinc-800 pt-2 space-y-1" data-testid="earnings-import-errors">{result.errors.map((e, i) => <div key={i} className="text-zinc-500">{e}</div>)}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
